import type {
  CompletionChunk,
  CompletionRequest,
  CompletionResult,
  LLMProvider,
  PreparedRequest,
  ProviderProfile,
  TransportResponse,
} from "@roqeet/core";
import { parseSSE } from "../sse.js";
import { getExactTokenCounter } from "../../tokenizer/index.js";

/**
 * Google Gemini Provider 어댑터 — 결정 #7.
 *
 * OpenAI와 다른 점:
 *  - 엔드포인트가 모델별 경로 + 키를 쿼리스트링으로 전달.
 *  - 메시지가 `contents: [{ role, parts: [{text}] }]` 구조이며 role은 user/model.
 *  - system은 `system_instruction` 필드로 분리.
 *  - 스트리밍은 `streamGenerateContent?alt=sse`.
 */
export class GoogleProvider implements LLMProvider {
  readonly kind = "google" as const;

  private baseUrl(profile: ProviderProfile): string {
    return (profile.baseUrl ?? "https://generativelanguage.googleapis.com/v1beta").replace(
      /\/+$/,
      "",
    );
  }

  prepare(req: CompletionRequest, profile: ProviderProfile, apiKey?: string): PreparedRequest {
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(profile.extraHeaders ?? {}),
    };

    const system = req.messages
      .filter((m) => m.role === "system")
      .map((m) => m.content)
      .join("\n\n");
    const contents = req.messages
      .filter((m) => m.role !== "system")
      .filter((m) => m.content.trim())
      .map((m) => ({
        role: m.role === "assistant" ? "model" : "user",
        parts: [{ text: m.content }],
      }));
    if (contents.length === 0) {
      const fallback = (req.prompt?.trim() || system.trim() || "Continue.").slice(0, 100_000);
      contents.push({ role: "user", parts: [{ text: fallback }] });
    }

    const body: Record<string, unknown> = {
      contents,
      generationConfig: mapGenConfig(req.params, req.model),
    };
    if (system) body["system_instruction"] = { parts: [{ text: system }] };

    const method = req.stream ? "streamGenerateContent" : "generateContent";
    const query = new URLSearchParams();
    if (apiKey) query.set("key", apiKey);
    if (req.stream) query.set("alt", "sse");

    const prepared: PreparedRequest = {
      url: `${this.baseUrl(profile)}/models/${encodeURIComponent(req.model)}:${method}?${query.toString()}`,
      method: "POST",
      headers,
      body: JSON.stringify(body),
    };
    if (req.signal) prepared.signal = req.signal;
    return prepared;
  }

  async *parseStream(res: TransportResponse): AsyncIterable<CompletionChunk> {
    if (!res.stream) {
      const result = await this.parseResult(res);
      if (result.text) yield { delta: result.text };
      return;
    }
    for await (const payload of parseSSE(res.stream)) {
      let json: GeminiResponse;
      try {
        json = JSON.parse(payload) as GeminiResponse;
      } catch {
        continue;
      }
      const text = extractText(json);
      const reasoning = extractReasoning(json);
      const usage = mapUsage(json.usageMetadata);
      if (text || reasoning || usage) {
        yield {
          delta: text,
          ...(reasoning ? { reasoningDelta: reasoning } : {}),
          ...(usage ? { usage } : {}),
        };
      }
    }
  }

  async parseResult(res: TransportResponse): Promise<CompletionResult> {
    const raw = (await res.text?.()) ?? "";
    if (!res.ok) {
      throw new Error(`Gemini 요청 실패 (HTTP ${res.status}): ${raw.slice(0, 500)}`);
    }
    const json = JSON.parse(raw) as GeminiResponse;
    const result: CompletionResult = {
      text: extractText(json),
      reasoning: extractReasoning(json),
      finishReason: mapFinish(json.candidates?.[0]?.finishReason),
    };
    if (json.usageMetadata) {
      result.usage = {
        promptTokens: json.usageMetadata.promptTokenCount,
        completionTokens: json.usageMetadata.candidatesTokenCount,
        reasoningTokens: json.usageMetadata.thoughtsTokenCount,
        totalTokens: json.usageMetadata.totalTokenCount,
      };
    }
    return result;
  }

  async countTokens(text: string, model: string): Promise<number> {
    // Gemini는 공식 tiktoken 인코딩이 없어 cl100k_base를 근사로 쓴다(결정 #12).
    // char/4 휴리스틱보다 정확하다. 로드 실패 시 모듈 내부에서 휴리스틱 폴백.
    const count = await getExactTokenCounter(model);
    return count(text);
  }

  async listModels(profile: ProviderProfile, apiKey?: string): Promise<string[]> {
    const models: string[] = [];
    let pageToken: string | undefined;

    for (let page = 0; page < 20; page++) {
      const query = new URLSearchParams({ pageSize: "1000" });
      if (apiKey) query.set("key", apiKey);
      if (pageToken) query.set("pageToken", pageToken);

      const res = await fetch(`${this.baseUrl(profile)}/models?${query.toString()}`);
      if (!res.ok) return unique(models);

      const json = (await res.json()) as GeminiModelsResult;
      for (const model of json.models ?? []) {
        const id = normalizeModelName(model.name);
        if (id && supportsGenerateContent(model)) models.push(id);
      }

      if (!json.nextPageToken) break;
      pageToken = json.nextPageToken;
    }

    return unique(models).sort();
  }
}

function normalizeModelName(name?: string): string {
  return name?.replace(/^models\//, "") ?? "";
}

function supportsGenerateContent(model: GeminiModel): boolean {
  const methods =
    model.supportedGenerationMethods ??
    model.supportedActions ??
    model.supported_actions ??
    [];
  return methods.length === 0 || methods.includes("generateContent");
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function extractText(json: GeminiResponse): string {
  const parts = json.candidates?.[0]?.content?.parts ?? [];
  return parts.filter((p) => !p.thought).map((p) => p.text ?? "").join("");
}

function extractReasoning(json: GeminiResponse): string {
  const parts = json.candidates?.[0]?.content?.parts ?? [];
  return parts.filter((p) => p.thought).map((p) => p.text ?? "").join("");
}

function mapGenConfig(p: CompletionRequest["params"], model: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.temperature != null) out["temperature"] = p.temperature;
  if (p.topP != null) out["topP"] = p.topP;
  if (p.topK != null) out["topK"] = p.topK;
  if (p.maxTokens != null) out["maxOutputTokens"] = p.maxTokens;
  if (p.stop && p.stop.length) out["stopSequences"] = p.stop;
  const thinkingConfig = thinkingConfigFor(model, p);
  if (thinkingConfig) out["thinkingConfig"] = thinkingConfig;
  if (p.extra) Object.assign(out, p.extra);
  return out;
}

function thinkingConfigFor(
  model: string,
  p: CompletionRequest["params"],
): Record<string, unknown> | undefined {
  if (p.reasoningBudgetTokens != null) return { thinkingBudget: p.reasoningBudgetTokens };
  if (p.reasoningLevel && p.reasoningLevel !== "auto") {
    if (p.reasoningLevel === "off") {
      return isGemini3Model(model.toLowerCase()) ? { thinkingLevel: "minimal" } : { thinkingBudget: 0 };
    }
    if (isGemini3Model(model.toLowerCase())) {
      return { thinkingLevel: p.reasoningLevel === "xhigh" ? "high" : p.reasoningLevel };
    }
    return { thinkingBudget: thinkingBudgetForLevel(p.reasoningLevel) };
  }
  return defaultThinkingConfig(model);
}

function defaultThinkingConfig(model: string): Record<string, unknown> | undefined {
  const id = model.toLowerCase();
  if (!id.includes("gemini")) return undefined;

  if (isGemini3Model(id)) {
    return { thinkingLevel: id.includes("pro") ? "low" : "minimal" };
  }
  if (id.includes("gemini-2.5")) {
    return id.includes("pro") ? { thinkingBudget: 128 } : { thinkingBudget: 0 };
  }
  return undefined;
}

function thinkingBudgetForLevel(level: string): number {
  switch (level) {
    case "minimal":
      return 0;
    case "low":
      return 1024;
    case "medium":
      return 4096;
    case "high":
      return 8192;
    case "xhigh":
      return 16384;
    default:
      return 0;
  }
}

function isGemini3Model(id: string): boolean {
  return /(?:^|[/_-])gemini-3(?:[.-]|$)/.test(id);
}

function mapFinish(reason?: string): CompletionResult["finishReason"] {
  switch (reason) {
    case "STOP":
      return "stop";
    case "MAX_TOKENS":
      return "length";
    case "SAFETY":
    case "RECITATION":
      return "content-filter";
    default:
      return reason ? "stop" : undefined;
  }
}

interface GeminiResponse {
  candidates?: {
    content?: { parts?: { text?: string; thought?: boolean; thoughtSignature?: string }[] };
    finishReason?: string;
  }[];
  usageMetadata?: GeminiUsageMetadata;
}

function mapUsage(usage?: GeminiUsageMetadata): CompletionResult["usage"] | undefined {
  if (!usage) return undefined;
  return {
    promptTokens: usage.promptTokenCount,
    completionTokens: usage.candidatesTokenCount,
    reasoningTokens: usage.thoughtsTokenCount,
    totalTokens: usage.totalTokenCount,
  };
}

interface GeminiUsageMetadata {
  promptTokenCount?: number;
  candidatesTokenCount?: number;
  thoughtsTokenCount?: number;
  totalTokenCount?: number;
}

interface GeminiModelsResult {
  models?: GeminiModel[];
  nextPageToken?: string;
}

interface GeminiModel {
  name?: string;
  supportedGenerationMethods?: string[];
  supportedActions?: string[];
  supported_actions?: string[];
}
